import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { preferenceOptions } from './mock';
import { createPersonalityResult } from '../services/api';
import { useToast } from '../hooks/use-toast';

const PersonalityTest = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const progress = ((currentQuestion + 1) / preferenceOptions.length) * 100;

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
  };

  const handleNext = async () => {
    if (selectedOption) {
      const newAnswers = {
        ...answers,
        [preferenceOptions[currentQuestion].id]: selectedOption
      };
      setAnswers(newAnswers);

      if (currentQuestion < preferenceOptions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setIsSubmitting(true);
        
        try {
          // Calculate personality based on answers
          const personalityScores = { comfort: 0, adventurous: 0, healthy: 0 };
          
          Object.values(newAnswers).forEach(answerId => {
            const option = preferenceOptions
              .flatMap(q => q.options)
              .find(opt => opt.id === answerId);
            if (option) {
              personalityScores[option.personality]++;
            }
          });

          const topPersonality = Object.entries(personalityScores)
            .sort(([,a], [,b]) => b - a)[0][0];

          // Save result to backend
          const result = await createPersonalityResult(topPersonality, newAnswers);
          
          toast({
            title: "Assessment Complete!",
            description: "Your food personality has been determined.",
          });

          navigate(`/results/${topPersonality}?id=${result.id}`);
        } catch (error) {
          // Calculate personality for fallback
          const personalityScores = { comfort: 0, adventurous: 0, healthy: 0 };
          
          Object.values(newAnswers).forEach(answerId => {
            const option = preferenceOptions
              .flatMap(q => q.options)
              .find(opt => opt.id === answerId);
            if (option) {
              personalityScores[option.personality]++;
            }
          });

          const topPersonality = Object.entries(personalityScores)
            .sort(([,a], [,b]) => b - a)[0][0];
            
          toast({
            title: "Error",
            description: "Failed to save your results. Please try again.",
            variant: "destructive",
          });
          
          // Fallback: save to localStorage and continue
          const result = {
            id: Date.now().toString(),
            personality: topPersonality,
            date: new Date().toISOString().split('T')[0],
            answers: newAnswers,
            shared: false
          };
          
          const savedResults = JSON.parse(localStorage.getItem('foodBuddyResults') || '[]');
          savedResults.push(result);
          localStorage.setItem('foodBuddyResults', JSON.stringify(savedResults));
          
          navigate(`/results/${topPersonality}?id=${result.id}`);
        } finally {
          setIsSubmitting(false);
        }
      }
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(null);
    } else {
      navigate('/');
    }
  };

  const currentQuestionData = preferenceOptions[currentQuestion];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Food Personality Assessment
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Choose the option that best represents your preferences
          </p>
          <div className="w-full max-w-md mx-auto">
            <Progress value={progress} className="h-3" />
            <p className="text-sm text-gray-500 mt-2">
              Question {currentQuestion + 1} of {preferenceOptions.length}
            </p>
          </div>
        </div>

        {/* Question */}
        <Card className="p-8 mb-8 bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <h2 className="text-2xl font-semibold text-gray-800 text-center mb-8">
            {currentQuestionData.question}
          </h2>

          {/* Options */}
          <div className="grid md:grid-cols-3 gap-6">
            {currentQuestionData.options.map((option) => (
              <div
                key={option.id}
                onClick={() => handleOptionSelect(option.id)}
                className={`relative cursor-pointer group transition-all duration-300 transform hover:scale-105 ${
                  selectedOption === option.id 
                    ? 'ring-4 ring-blue-500 ring-opacity-50' 
                    : 'hover:shadow-lg'
                }`}
              >
                <Card className="overflow-hidden border-2 border-transparent group-hover:border-blue-200">
                  <div className="aspect-square relative">
                    <img 
                      src={option.image} 
                      alt={option.label}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    
                    {/* Selection indicator */}
                    {selectedOption === option.id && (
                      <div className="absolute top-4 right-4 animate-scale-in">
                        <CheckCircle className="w-8 h-8 text-white bg-blue-500 rounded-full" />
                      </div>
                    )}
                    
                    {/* Label */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-lg font-semibold text-white">
                        {option.label}
                      </h3>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            onClick={handleBack}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentQuestion === 0 ? 'Back to Home' : 'Previous'}
          </Button>

          <Button
            onClick={handleNext}
            disabled={!selectedOption || isSubmitting}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            {isSubmitting ? 'Processing...' : currentQuestion === preferenceOptions.length - 1 ? 'Get My Results' : 'Next'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalityTest;